import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Paper, Typography, Box, Link, Checkbox, FormControlLabel } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Email as EmailIcon, Lock as LockIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import Input from '../common/Input';
import Button from '../common/Button';
import authService from '../../services/auth';
import { focusRing, publicLink } from './landingStyles';

const AuthCard: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      remember: false,
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email address').required('Email is required'),
      password: Yup.string().required('Password is required'),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const response = await authService.login({
          email: values.email,
          password: values.password,
        });
        authService.setToken(response.token, values.remember);
        navigate('/dashboard');
      } catch (error: any) {
        formik.setErrors({
          email:
            error.response?.data?.error?.message ||
            error.response?.data?.message ||
            'Login failed',
        });
      } finally {
        setLoading(false);
      }
    },
  });

  const handleGoogleLogin = () => {
    authService.googleAuth();
  };

  return (
    <Paper
      id="sign-in"
      component="section"
      aria-labelledby="sign-in-title"
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        scrollMarginTop: 88,
        boxShadow: 2,
      }}
    >
      <Typography id="sign-in-title" variant="h5" component="h2" sx={{ fontWeight: 800 }}>
        Sign in
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Welcome back. Pick up the deal you were skouting.
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <Input
          label="Email"
          name="email"
          type="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          error={formik.touched.email ? formik.errors.email : undefined}
          startIcon={<EmailIcon />}
          required
        />

        <Input
          label="Password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          value={formik.values.password}
          onChange={formik.handleChange}
          error={formik.touched.password ? formik.errors.password : undefined}
          startIcon={<LockIcon />}
          endIcon={showPassword ? <VisibilityOff /> : <Visibility />}
          onEndIconClick={() => setShowPassword(!showPassword)}
          required
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                name="remember"
                checked={formik.values.remember}
                onChange={formik.handleChange}
              />
            }
            label="Remember me"
          />
          <Link component={RouterLink} to="/forgot-password" variant="body2" sx={{ ...publicLink, ...focusRing }}>
            Forgot password?
          </Link>
        </Box>

        <Button type="submit" fullWidth loading={loading} variant="contained" color="primary">
          Sign In
        </Button>
      </form>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          New here?{' '}
          <Link component={RouterLink} to="/signup" sx={{ ...publicLink, ...focusRing }}>
            Create your free account
          </Link>
        </Typography>
      </Box>

      <Button
        fullWidth
        variant="outlined"
        onClick={handleGoogleLogin}
        startIcon={
          <img src="https://www.google.com/favicon.ico" alt="" style={{ width: 20, height: 20 }} />
        }
      >
        Continue with Google
      </Button>
    </Paper>
  );
};

export default AuthCard;
